import { memo, useState, useRef, useEffect } from "react";
import { Chip, Grid2, IconButton, Typography, TextField, useTheme, Dialog, DialogTitle, DialogContent, DialogActions, Button, Tooltip } from "@mui/material";
import { icon } from "@/app/style/icons";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import ChatActionIcons from "../ChatActionIcons";
import { useMutation } from "@apollo/client";
import { RENAME_CHAT } from "@chat/graphql/mutations/renameChat";
import { TOGGLE_CHAT_PRIVACY } from "@chat/graphql/mutations/toggleChatPrivacy";
import { FETCH_CHATS } from "@chat/graphql/queries/fetchChats";
import { Mutation } from "@/app/graphql/codegen";

interface ChatDetailsHeaderProps {
    id: string;
    name: string | null | undefined;
    isPrivate: boolean;
}

const ChatDetailsHeader = memo(({ name, id, isPrivate }: ChatDetailsHeaderProps) => {
    // Local state for editing
    const [isEditing, setIsEditing] = useState(false);
    const [editedName, setEditedName] = useState(name || "");
    const [privacyDialogOpen, setPrivacyDialogOpen] = useState(false);
    const [showTooltip, setShowTooltip] = useState<boolean>(false);
    const titleRef = useRef<HTMLElement>(null);

    const theme = useTheme();

    const [renameChatMutation] = useMutation<Pick<Mutation, "renameChat">, { id: string; name: string }>(RENAME_CHAT, {
        refetchQueries: [{ query: FETCH_CHATS }],
    });

    const [toggleChatPrivacyMutation] = useMutation(TOGGLE_CHAT_PRIVACY, {
        refetchQueries: [{ query: FETCH_CHATS }],
    });

    const renameChat = async (id: string, name: string) => {
        const result = await renameChatMutation({
            variables: { id, name },
            refetchQueries: [{ query: FETCH_CHATS }],
        });
        return result.data?.renameChat;
    };

    const toggleChatPrivacy = async (id: string) => {
        const result = await toggleChatPrivacyMutation({
            variables: { id },
            refetchQueries: [{ query: FETCH_CHATS }],
        });
        return result.data?.toggleChatPrivacy;
    };

    const handleEditChatName = () => {
        setEditedName(name || "");
        setIsEditing(true);
    };

    const handleConfirmRename = async () => {
        await renameChat(id, editedName);
        setIsEditing(false);
    };

    const handleCancelRename = () => {
        setIsEditing(false);
    };

    const handleTogglePrivacy = () => {
        setPrivacyDialogOpen(true);
    };

    const handleConfirmPrivacyChange = async () => {
        await toggleChatPrivacy(id);
        setPrivacyDialogOpen(false);
    };

    const handleCancelPrivacyChange = () => {
        setPrivacyDialogOpen(false);
    };

    // Check if text is truncated and should show tooltip
    useEffect(() => {
        if (titleRef.current && name && !isEditing) {
            const element = titleRef.current;
            const isOverflowing = element.scrollWidth > element.clientWidth;
            setShowTooltip(isOverflowing);
        }
    }, [name, isEditing]);

    return (
        <>
            <Grid2 container size={12}>
                <Grid2 container size={"grow"} spacing={0.5} alignItems={"center"}>
                    <Grid2>
                        {isEditing ? (
                            <TextField
                                value={editedName}
                                onChange={(e) => setEditedName(e.target.value)}
                                variant="standard"
                                sx={{
                                    "& input": {
                                        ...theme.typography.h4,
                                    },
                                }}
                            />
                        ) : (
                            <Tooltip 
                                title={name || "Unnamed Chat"} 
                                disableHoverListener={!showTooltip}
                                placement="bottom"
                            >
                                <Typography 
                                    ref={titleRef}
                                    variant="h4"
                                    sx={{
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        cursor: showTooltip ? "help" : "default",
                                        maxWidth: "100%"
                                    }}
                                >
                                    {name || "Unnamed Chat"}
                                </Typography>
                            </Tooltip>
                        )}
                    </Grid2>

                    <Grid2>
                        {isEditing ? (
                            <>
                                <IconButton onClick={handleConfirmRename}>
                                    <icon.confirm sx={{ fontSize: 22, color: "semantic.icon.functional" }} />
                                </IconButton>
                                <IconButton onClick={handleCancelRename}>
                                    <icon.cancel sx={{ fontSize: 22, color: "semantic.icon.functional" }} />
                                </IconButton>
                            </>
                        ) : (
                            <IconButton onClick={handleEditChatName}>
                                <icon.edit sx={{ fontSize: 22, color: "semantic.icon.functional" }} />
                            </IconButton>
                        )}
                    </Grid2>

                    <Grid2>
                        <Chip
                            size="small"
                            label={isPrivate ? "Privat" : "Öffentlich"}
                            color={isPrivate ? "secondary" : "default"}
                            onClick={handleTogglePrivacy}
                            icon={isPrivate ? <LockIcon fontSize="small" sx={{ fontSize: 12 }} /> : <LockOpenIcon fontSize="small" sx={{ fontSize: 12 }} />}
                        />
                    </Grid2>
                </Grid2>

                <ChatActionIcons chatId={id} />
            </Grid2>

            <Dialog open={privacyDialogOpen} onClose={handleCancelPrivacyChange}>
                <DialogTitle>{isPrivate ? "Chat öffentlich machen?" : "Chat privat machen?"}</DialogTitle>
                <DialogContent>
                    {isPrivate ? (
                        <Typography>Dieser Chat wird für alle Benutzer in Ihrer Organisation sichtbar sein. Sind Sie sicher, dass Sie ihn öffentlich machen möchten?</Typography>
                    ) : (
                        <Typography>Dieser Chat wird nur für Sie sichtbar sein. Sind Sie sicher, dass Sie ihn privat machen möchten?</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelPrivacyChange}>Abbrechen</Button>
                    <Button onClick={handleConfirmPrivacyChange} variant="contained" color="secondary">
                        Bestätigen
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
});

export default ChatDetailsHeader;
